import { MAX_CHARACTER_MEDIA_BYTES, type Character } from '@moussenaires/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiClientError, mediaSrc } from '../api/client.js';
import { feedApi } from '../api/feed.js';
import { rosterApi } from '../api/roster.js';
import './CharacterMediaField.css';

/**
 * A character's "hero" media: a GIF or short mp4 clip that replaces its
 * static Armory render once its roster card is focused. Reuses the forum's
 * own upload endpoint and storage quota (`POST /api/media`) — this just
 * layers a tighter size ceiling and the "attach to a character" step on
 * top of it.
 */
export function CharacterMediaField({ character }: { character: Character }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const setMediaMutation = useMutation({
    mutationFn: (mediaId: number | null) => rosterApi.setCharacterMedia(character.id, mediaId),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['roster'] });
    },
    onError: (uploadError: unknown) => {
      setError(
        uploadError instanceof ApiClientError
          ? t(`errors.${uploadError.code}`, { defaultValue: t('media.uploadFailed') })
          : t('media.uploadFailed'),
      );
    },
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => feedApi.upload(file),
    onSuccess: (media) => setMediaMutation.mutate(media.id),
    onError: (uploadError: unknown) => {
      setError(
        uploadError instanceof ApiClientError
          ? t(`errors.${uploadError.code}`, { defaultValue: t('media.uploadFailed') })
          : t('media.uploadFailed'),
      );
    },
  });

  const isBusy = uploadMutation.isPending || setMediaMutation.isPending;

  function handleFile(file: File) {
    if (file.size > MAX_CHARACTER_MEDIA_BYTES) {
      setError(t('roster.heroMediaTooLarge'));
      return;
    }
    uploadMutation.mutate(file);
  }

  return (
    <div className="character-media-field">
      <label>{t('roster.heroMedia')}</label>
      <p className="character-media-hint">{t('roster.heroMediaHint')}</p>

      {character.media ? (
        <div className="character-media-current">
          {character.media.kind === 'video' ? (
            <video
              src={mediaSrc(character.media.url)}
              className="character-media-preview"
              muted
              loop
              autoPlay
              playsInline
            />
          ) : (
            <img src={mediaSrc(character.media.url)} alt="" className="character-media-preview" />
          )}
          <button
            type="button"
            className="btn btn-ghost"
            disabled={isBusy}
            onClick={() => setMediaMutation.mutate(null)}
          >
            <Trash2 size={14} /> {t('common.delete')}
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="btn"
          disabled={isBusy}
          onClick={() => inputRef.current?.click()}
        >
          <Upload size={14} /> {isBusy ? t('roster.syncing') : t('roster.heroMediaUpload')}
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        hidden
        accept="video/mp4,image/gif"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) handleFile(file);
          event.target.value = '';
        }}
      />

      {error && <p className="field-error">{error}</p>}
    </div>
  );
}
